import type { WireConditionEntry } from '@danda/shared';
import { StatusPill, type StatusPillSize } from '@/components/ui/status-pill';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

interface ConditionBadgeProps {
  condition: WireConditionEntry;
  size?: StatusPillSize;
}

// Captive-lifecycle conditions get name-based color overrides. `chained` uses
// gold to read as "captive / awaiting rescue" without the threat-tier red that
// hostile/wounded states use; `freed` flips to green as a persistent "rescued"
// marker. Solid pill style — matches health/attitude badges (e.g. WOUNDED).
const CONDITION_COLOR_OVERRIDES: Record<string, string> = {
  chained: 'bg-amber-500 text-white',
  freed: 'bg-green-600 text-white',
};

const CONDITION_LABEL_PREFIX: Record<string, string> = {
  chained: '⛓️ ',
  freed: '✨ ',
  // Burning (Searing Smite fire DoT): 🔥 reads the recurring-damage tier; the
  // default orange tint already fits fire, so no color override needed.
  burning: '🔥 ',
};

// Default impairment tint: orange reads as "bad, but not threat tier". Reserves
// red for the health/threat ladder (DYING/CRITICAL/WOUNDED) and enemy attitude,
// so a PRONE/RESTRAINED character doesn't visually scream "dying" next to one
// who actually is.
const DEFAULT_TINT = 'bg-orange-500 text-white';

/**
 * Renders a condition badge that matches the StatusPill shape (e.g. DYING),
 * with an impairment-tier orange tint by default. Captive-lifecycle overrides
 * (chained/freed) ride above; object conditions with duration/save info show
 * a tooltip on hover.
 */
export function ConditionBadge({ condition, size = 'sm' }: ConditionBadgeProps) {
  const isSimple = typeof condition === 'string';
  const name = isSimple ? condition : condition.name;
  const lowerName = name.toLowerCase();
  const tintClass = CONDITION_COLOR_OVERRIDES[lowerName] ?? DEFAULT_TINT;
  const labelPrefix = CONDITION_LABEL_PREFIX[lowerName] ?? '';
  const displayName = `${labelPrefix}${name.toUpperCase()}`;

  const pill = (extra?: string) => (
    <StatusPill size={size} className={extra ? `${tintClass} ${extra}` : tintClass}>
      {displayName}
    </StatusPill>
  );

  if (isSimple) return pill();

  const { duration_rounds, save_timing, save_dc, save_ability, source } = condition;
  const tooltipLines: string[] = [];
  if (source) tooltipLines.push(`Source: ${source}`);
  if (duration_rounds !== undefined) {
    tooltipLines.push(`Duration: ${duration_rounds} round${duration_rounds !== 1 ? 's' : ''}`);
  }
  if (save_timing) {
    const timingLabels: Record<string, string> = {
      start_of_turn: 'Save at start of turn',
      end_of_turn: 'Save at end of turn',
    };
    tooltipLines.push(timingLabels[save_timing] || save_timing);
  }
  if (save_dc !== undefined && save_ability) {
    tooltipLines.push(`Save: DC ${save_dc} ${String(save_ability).toUpperCase()}`);
  }

  if (tooltipLines.length === 0) return pill();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{pill('cursor-help')}</TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            {tooltipLines.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
